import API_ENDPOINTS from "@/common/api/apiEndpoints";
import httpClient from "@/common/api/httpClient";
import httpMethods from "@/common/api/httpMethods";
import {
  resultEndpoints,
  type ResultSource,
} from "@/common/services/result-source";
import type {
  AdhocRunConfiguration,
  Agents,
  Environment,
  TestCaseDataDrivenResults,
  TestCaseResults,
  TestStepResults,
  Paginated,
} from "@/common/types/generated";

type GenericQueryParams = {
  query?: string;
  sort?: string;
  page?: number;
  size?: number;
  [key: string]: string | number | boolean | null | undefined;
};

export const buildSpecialQueryParams = (params: GenericQueryParams): string => {
  const { query, ...rest } = params;

  const searchParams = new URLSearchParams();

  Object.entries(rest).forEach(([key, value]) => {
    if (value !== null && value !== undefined) {
      searchParams.set(key, String(value));
    }
  });

  // Manually prepend `query` if present
  const queryPart = query ? `query=${query}` : "";

  return `?${[queryPart, searchParams.toString()].filter(Boolean).join("&")}`;
};

// Agents
export const fetchIntegrationSettings = () => {
  const endpoint = `${API_ENDPOINTS.TESTCASE.integrationSettings}`;
  return httpClient(httpMethods.GET, endpoint);
};

export const fetchBrowserStackDevices = (
  configId: number | string,
  platform?: string,
) => {
  const params = new URLSearchParams();
  if (platform) params.set("platform", platform);
  const endpoint = `${API_ENDPOINTS.TESTCASE.integrationSettings}/${configId}/browserstack_devices${params.toString() ? `?${params.toString()}` : ""}`;
  return httpClient<any[]>(httpMethods.GET, endpoint);
};

export const fetchBrowserStackBrowsers = (
  configId: number | string,
  filters?: {
    platform?: string;
    device?: string;
    osVersion?: string;
    browser?: string;
  },
) => {
  const params = new URLSearchParams();
  if (filters?.platform) params.set("platform", filters.platform);
  if (filters?.device) params.set("device", filters.device);
  if (filters?.osVersion) params.set("os_version", filters.osVersion);
  if (filters?.browser) params.set("browser", filters.browser);
  const endpoint = `${API_ENDPOINTS.TESTCASE.integrationSettings}/${configId}/browserstack_browsers${params.toString() ? `?${params.toString()}` : ""}`;
  return httpClient<any[]>(httpMethods.GET, endpoint);
};

export const fetchSauceLabsDevices = (
  configId: number | string,
  platform?: string,
) => {
  const params = new URLSearchParams();
  if (platform) params.set("platform", platform);
  const endpoint = `${API_ENDPOINTS.TESTCASE.integrationSettings}/${configId}/saucelabs_devices${params.toString() ? `?${params.toString()}` : ""}`;
  return httpClient<any[]>(httpMethods.GET, endpoint);
};

export const fetchLambdaTestDevices = (
  configId: number | string,
  platform?: string,
) => {
  const params = new URLSearchParams();
  if (platform) params.set("platform", platform);
  const endpoint = `${API_ENDPOINTS.TESTCASE.integrationSettings}/${configId}/lambdatest_devices${params.toString() ? `?${params.toString()}` : ""}`;
  return httpClient<any[]>(httpMethods.GET, endpoint);
};

export const fetchAwsDeviceFarmDevices = (
  configId: number | string,
  platform?: string,
) => {
  const params = new URLSearchParams();
  if (platform) params.set("platform", platform);
  const endpoint = `${API_ENDPOINTS.TESTCASE.integrationSettings}/${configId}/awsdevicefarm_devices${params.toString() ? `?${params.toString()}` : ""}`;
  return httpClient<any[]>(httpMethods.GET, endpoint);
};

export const fetchMobileProviderCapabilities = (configId: number | string) => {
  const endpoint = `${API_ENDPOINTS.TESTCASE.integrationSettings}/${configId}/mobile_capabilities`;
  return httpClient<any>(httpMethods.GET, endpoint);
};

export const fetchSauceLabsBrowsers = (
  configId: number | string,
  filters?: {
    platform?: string;
    device?: string;
    osVersion?: string;
    browser?: string;
  },
) => {
  const params = new URLSearchParams();
  if (filters?.platform) params.set("platform", filters.platform);
  if (filters?.device) params.set("device", filters.device);
  if (filters?.osVersion) params.set("os_version", filters.osVersion);
  if (filters?.browser) params.set("browser", filters.browser);
  const endpoint = `${API_ENDPOINTS.TESTCASE.integrationSettings}/${configId}/saucelabs_browsers${params.toString() ? `?${params.toString()}` : ""}`;
  return httpClient<any[]>(httpMethods.GET, endpoint);
};

export const postIntegrationSettings = (payload: Record<string, unknown>) => {
  const endpoint = `${API_ENDPOINTS.TESTCASE.integrationSettings}`;
  return httpClient<void>(httpMethods.POST, endpoint, payload);
};

export const updateIntegrationSettings = (
  integrationId: number | string,
  payload: Record<string, unknown>,
) => {
  const endpoint = `${API_ENDPOINTS.TESTCASE.integrationSettings}/${integrationId}`;
  return httpClient<void>(httpMethods.PUT, endpoint, payload);
};

export const setIntegrationEnabled = (
  integrationId: number | string,
  enabled: boolean,
) => {
  const endpoint = `${API_ENDPOINTS.TESTCASE.integrationSettings}/${integrationId}/enabled`;
  return httpClient<void>(httpMethods.PATCH, endpoint, { enabled });
};

export const deleteIntegrationSettings = (integrationId: number | string) => {
  const endpoint = `${API_ENDPOINTS.TESTCASE.integrationSettings}/${integrationId}`;
  return httpClient<void>(httpMethods.DELETE, endpoint);
};

export const checkEmptyElement = (
  testCaseId: number | string,
  versionId: number | string,
) => {
  const endpoint = `${API_ENDPOINTS.ELEMENTS.checkEmptyElement}${testCaseId}?workspaceVersionId=${versionId}`;
  return httpClient(httpMethods.GET, endpoint);
};

export const checkURLAccess = (testCaseId: number | string) => {
  const endpoint = `${API_ENDPOINTS.TESTCASE.validateURL}${testCaseId}`;
  return httpClient(httpMethods.GET, endpoint);
};

export const fetchAgents = (query?: string) => {
  let endpoint = API_ENDPOINTS.AGENT.agentURL;
  if (query && query.trim()) {
    endpoint += `?query=${encodeURIComponent(query)}`;
  }
  return httpClient<Paginated<Agents>>(httpMethods.GET, endpoint);
};

export const fetchAdhocRunConfig = (applicationType: string) => {
  const endpoint = `${API_ENDPOINTS.AGENT.adhocConfig}/${applicationType}`;
  return httpClient<AdhocRunConfiguration>(httpMethods.GET, endpoint);
};

export const createAdhocRunConfig = (
  payload: Partial<AdhocRunConfiguration>,
) => {
  const endpoint = `${API_ENDPOINTS.AGENT.adhocConfig}`;
  return httpClient<void>(httpMethods.POST, endpoint, payload);
};

export const updateAdhocRunConfig = (
  configId: number | string,
  payload: Partial<AdhocRunConfiguration>,
) => {
  const endpoint = `${API_ENDPOINTS.AGENT.adhocConfig}/${configId}`;
  return httpClient<void>(httpMethods.PUT, endpoint, payload);
};

export const deleteAdhocRunConfig = (configId: number | string) => {
  const endpoint = `${API_ENDPOINTS.AGENT.adhocConfig}/${configId}`;
  return httpClient<void>(httpMethods.DELETE, endpoint);
};

export const fetchEnvironment = () => {
  const endpoint = `${API_ENDPOINTS.ENV.environments}`;
  return httpClient<Paginated<Environment>>(httpMethods.GET, endpoint);
};

export const fetchTestCaseResults = (params: GenericQueryParams) => {
  const endpoint = `${API_ENDPOINTS.TESTCASE.testCaseResults}${buildSpecialQueryParams(params)}`;
  return httpClient<Paginated<TestCaseResults>>(httpMethods.GET, endpoint);
};

export const fetchTestCaseResultById = (
  testCaseResultId: number | string,
  source: ResultSource = "plan",
) => {
  const endpoint = `${resultEndpoints(source).caseResults}/${testCaseResultId}`;
  return httpClient<TestCaseResults>(httpMethods.GET, endpoint);
};

//
export const createDryRunPlan = (payload: Record<string, unknown>) => {
  const endpoint = `${API_ENDPOINTS.TESTCASE.dryTestPlan}`;
  return httpClient<void>(httpMethods.POST, endpoint, payload);
};

// Test Results
export const fetchTestStepResults = (
  testCaseResultId: number | string,
  source: ResultSource = "plan",
) => {
  const endpoint = `${resultEndpoints(source).stepResults}?query=groupResultId:null,testCaseResultId:${testCaseResultId}`;
  return httpClient<Paginated<TestStepResults>>(httpMethods.GET, endpoint);
};

export const fetchStepGroupChildResults = (
  groupResultId: number | string,
  testCaseResultId: number | string,
  source: ResultSource = "plan",
) => {
  const endpoint = `${resultEndpoints(source).stepResults}?query=groupResultId:${groupResultId},testCaseResultId:${testCaseResultId}`;
  return httpClient<Paginated<TestStepResults>>(httpMethods.GET, endpoint);
};

export const fetchTestStepResultById = (
  testStepId: number | string,
  source: ResultSource = "plan",
) => {
  const endpoint = `${resultEndpoints(source).stepResults}/${testStepId}`;
  return httpClient<TestStepResults>(httpMethods.GET, endpoint);
};

export const fetchDataDrivenResults = (
  testCaseResultId: number | string,
  source: ResultSource = "plan",
) => {
  const endpoint = `${resultEndpoints(source).dataDrivenResults}?query=,testCaseResultId:${testCaseResultId}`;
  return httpClient<Paginated<TestCaseDataDrivenResults>>(
    httpMethods.GET,
    endpoint,
  );
};

export const fetchChildTestCaseResults = (
  parentId: number | string,
  source: ResultSource = "plan",
) => {
  const endpoint = `${resultEndpoints(source).caseResults}?query=parentId:${parentId}&sort=id,asc`;
  return httpClient<Paginated<TestCaseResults>>(httpMethods.GET, endpoint);
};
export const fetchRunHistory = (
  testCaseId: number | string,
  limit = 10,
  source: ResultSource = "plan",
) => {
  const endpoint = `${resultEndpoints(source).caseResults}/history/${testCaseId}?limit=${limit}`;
  return httpClient(httpMethods.GET, endpoint);
};

export const fetchFlakyScore = (
  testCaseId: number | string,
  window = 10,
  source: ResultSource = "plan",
) => {
  const endpoint = `${resultEndpoints(source).caseResults}/flaky/${testCaseId}?window=${window}`;

  return httpClient(httpMethods.GET, endpoint);
};

// Stop Dry Run
export const stopDryTestRun = (
  testPlanResultId: number | string,
  payload: Record<string, unknown>,
) => {
  const endpoint = `${API_ENDPOINTS.TESTPLAN.testPlanResult}/${testPlanResultId}`;
  return httpClient<void>(httpMethods.PUT, endpoint, payload);
};

// Stop a slim ad-hoc run (no test_plan_result exists, so it is keyed on the
// test_case_result id). Hits the agent stop endpoint which writes the stop flag
// the execution engine polls between steps.
export const stopAdHocRun = (testCaseResultId: number | string) => {
  const endpoint = `agent/api/testCaseRuns/stop/${testCaseResultId}`;
  return httpClient<void>(httpMethods.POST, endpoint, {});
};

// Update Manual Steps
export const updateManualSteps = (
  testStepResultId: number | string,
  payload: Partial<TestStepResults>,
) => {
  const endpoint = `${API_ENDPOINTS.TESTRESULTS.testStepResults}/${testStepResultId}`;
  return httpClient<void>(httpMethods.PUT, endpoint, payload);
};
